<template>
  <div>
    <user-transaction-form
      :transaction-type="transactionType"
      :is-side-bar-active.sync="isAddMoneyBarActive"
      @refetch-user-wallet="$emit('refetch-user-wallet')"
    />
    <b-overlay
      variant="white"
      spinner-variant="primary"
      blur="0"
      opacity="0.75"
      rounded="sm"
      :show="loadingTransactions"
    >
      <b-card>
        <b-row>
          <b-col
            md="12"
            class="d-flex justify-content-end align-items-center"
          >
            <h2>Transactions History</h2>
            <v-select
              v-model="queryParams.transaction_type_id"
              style="flex:1"
              class="mx-1"
              :options="types"
              option-label="label"
              :reduce="val => val.id"
              placeholder="Search by account name"
            />
            <b-button
              class="mr-1"
              variant="outline-primary"
              @click="$emit('refetch-user-transactions', queryParams)"
            >
              Search
            </b-button>
            <b-button
              v-if="$can('write', 'user-wallet-transactions')"
              variant="success"
              class="mr-2"
              @click="selectTransaction('add')"
            >
              Add Money
            </b-button>
            <b-button
              v-if="$can('write', 'user-wallet-transactions')"
              variant="primary"
              class="mr-2"
              @click="selectTransaction('deduct')"
            >
              Deduct Money
            </b-button>
            <feather-icon
              style="cursor:pointer"
              size="25"
              icon="DownloadIcon"
              @click="downloadTransactions"
            />
          </b-col>
        </b-row>
        <b-table
          responsive
          :items="tableData"
          :fields="tableColumns"
          show-empty
          empty-text="No transactions found"
          class="mt-2"
        >
          <template #cell(created_at)="data">
            {{ `${data.item.created_at}Z` | formatDate }}
          </template>
          <template #cell(remark_text)="data">
            <component
              :is="data.item.service == 'trips' ? 'b-link': 'div' "
              :to="{ path: `/trips/${data.item.external_reference_id}` }"
              :class="data.item.service == 'trips' ? 'linkStyle' : ''"
            >
              {{ data.item.remark_text }}
            </component>
          </template>
        </b-table>
        <base-pagination
          v-model="queryParams.page"
          :total="total"
          @new-per-page="queryParams.size = $event"
        />
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  reactive, watch, toRef, computed, ref, onUnmounted,
} from '@vue/composition-api'
import {
  BOverlay, BCard, BCardTitle, BTable, BRow, BCol, BButton, BLink, BFormInput, BInputGroupAppend, BInputGroup,
} from 'bootstrap-vue'
import moment from 'moment'
import vSelect from 'vue-select'
import BasePagination from '@/UI/BasePagination.vue'
import UserTransactionForm from './UserTransactionForm.vue'

export default {
  components: {
    UserTransactionForm,
    BOverlay,
    BCard,
    vSelect,
    BCardTitle,
    BTable,
    BRow,
    BCol,
    BButton,
    BasePagination,
    BLink,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
  },
  props: {
    loadingTransactions: {
      type: Boolean,
      default: false,
    },
    transactionsData: {
      type: Object,
      default: () => {},
    },

  },
  setup(props, ctx) {
    const isAddMoneyBarActive = ref(false)
    const transactionType = ref('')
    const selectTransaction = type => {
      transactionType.value = type
      isAddMoneyBarActive.value = true
    }
    const tableColumns = [
      { key: 'created_at', label: 'Date/Time' },
      { key: 'transaction_type_label', label: 'Account Name' },
      { key: 'credit_amount', label: 'Credit' },
      { key: 'debit_amount', label: 'Debit' },
      { key: 'added_by', label: 'Added By' },
      { key: 'remark_text', label: 'Remark' },
    ]
    const queryParams = reactive({
      page: 1,
      size: 10,
      transaction_type_id: null,
    })
    const total = ref(0)

    onUnmounted(() => {
      ctx.root.$store.commit('users/SET_USER_TRANSACTIONS', null)
    })
    const tableData = computed(() => {
      if (props.transactionsData) {
        total.value = props.transactionsData.total
      }
      return props.transactionsData?.transactions
    })
    const types = computed(() => ctx.root.$store.getters['transactionTypes/getTransactionTypes'] || [])
    watch([toRef(queryParams, 'page'), toRef(queryParams, 'size')], () => {
      ctx.emit('refetch-user-transactions', queryParams)
    })
    const downloadTransactions = () => {
      let csv = 'Date & Time,Account Name,Credit Amount,Debit Amount, Added By, Remark Text\n'

      const data = tableData.value.map(row => ({
        created_at: moment(row.created_at).format('dddd, MMMM Do YYYY, h:mm:ss a'),
        transaction_type_label: row.transaction_type_label,
        credit_amount: row.credit_amount,
        debit_amount: row.debit_amount,
        added_by: row.added_by,
        remark_text: row.remark_text,
      }))

      // Loop through the array
      data.forEach(row => {
        const rowValues = Object.entries(row)
        const rowLength = rowValues.length
        // Loop through each object
        rowValues.forEach((key, index) => {
          // Last item reached, set a new line
          if (index + 1 === rowLength) {
            csv += `${key[1]}\n`
            return
          }
          // escaped the commas with the date key
          if (typeof key[1] === 'string' && key[1].includes(',')) {
            // eslint-disable-next-line no-param-reassign
            key[1] = key[1].replaceAll(',', '')
          }
          // assign a comma for each cell
          csv += `${key[1]},`
        })
      })
      const link = document.createElement('a')
      link.id = 'download-csv'
      link.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(csv)}`)
      // eslint-disable-next-line no-underscore-dangle
      link.setAttribute('download', `${ctx.root._route.params.id}.csv`)
      document.body.appendChild(link)
      document.querySelector('#download-csv').click()
    }
    return {
      isAddMoneyBarActive,
      selectTransaction,
      transactionType,
      tableColumns,
      queryParams,
      tableData,
      types,
      total,
      downloadTransactions,
    }
  },
}
</script>

<style scoped>

.linkStyle {
  color: blue;
}
</style>
